import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Cards, CalendarCheck, CaretDown, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACCENTS = { work: "#10B981", relationships: "#EC4899", prosperity: "#F59E0B" };
const STATUS_STYLES = {
  confirmed: "text-work border-work/40 bg-work/10",
  pending_payment: "text-prosper border-prosper/40 bg-prosper/10",
  pending_verification: "text-prosper border-prosper/40 bg-prosper/10",
  cancelled: "text-red-400 border-red-500/40 bg-red-500/10",
  rejected: "text-red-400 border-red-500/40 bg-red-500/10",
};

function renderProse(text) {
  return text.split("\n").map((line, i) => {
    if (/^#{1,4}\s/.test(line)) return <h3 key={i}>{line.replace(/^#{1,4}\s+/, "")}</h3>;
    if (line.trim() === "") return null;
    return <p key={i}>{line.replace(/\*\*(.*?)\*\*/g, "$1")}</p>;
  });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [readings, setReadings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/readings").then(({ data }) => setReadings(data)).catch(() => {});
    loadBookings();
  }, []);

  const loadBookings = () => api.get("/bookings").then(({ data }) => setBookings(data)).catch(() => {});

  const verifyBooking = async (id, approve) => {
    try {
      await api.patch(`/bookings/${id}/verify`, { approve });
      toast.success(approve ? "Booking confirmed — client will be notified." : "Booking rejected.");
      loadBookings();
    } catch (e) {
      toast.error("Action failed. Try again.");
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 lg:px-10 max-w-6xl mx-auto" data-testid="dashboard-page">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gold mb-4">My Journey</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter">
        Welcome back, <span className="gold-text italic">{user?.name || "Seeker"}</span>
      </h1>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="bg-card border border-white/10 p-6" data-testid="stat-readings-count">
          <Cards size={24} weight="duotone" className="text-gold" />
          <p className="font-heading text-4xl font-bold mt-3">{readings.length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Readings drawn</p>
        </div>
        <div className="bg-card border border-white/10 p-6" data-testid="stat-bookings-count">
          <CalendarCheck size={24} weight="duotone" className="text-love" />
          <p className="font-heading text-4xl font-bold mt-3">{bookings.length}</p>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1">Sessions booked</p>
        </div>
        <Link to="/reading" className="hover-lift bg-card border border-white/10 p-6 flex flex-col justify-between" data-testid="dashboard-new-reading-link">
          <Sparkle size={24} weight="duotone" className="text-work" />
          <p className="text-xs uppercase tracking-[0.15em] text-foreground mt-3">New reading →</p>
        </Link>
        <Link to="/book" className="hover-lift bg-card border border-white/10 p-6 flex flex-col justify-between" data-testid="dashboard-book-link">
          <CalendarCheck size={24} weight="duotone" className="text-prosper" />
          <p className="text-xs uppercase tracking-[0.15em] text-foreground mt-3">Book a call →</p>
        </Link>
      </div>

      <h2 className="font-heading text-3xl font-semibold tracking-tight mt-16 mb-6">Reading Archive</h2>
      {readings.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center" data-testid="readings-empty-state">
          <p className="text-sm text-muted-foreground">No readings yet. The deck awaits.</p>
          <Link to="/reading" className="inline-block mt-4 text-xs uppercase tracking-[0.2em] text-gold hover:underline">
            Draw your first spread →
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="readings-list">
          {readings.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-card border border-white/10"
              data-testid={`reading-item-${i}`}
            >
              <button
                onClick={() => setOpen(open === r.id ? null : r.id)}
                data-testid={`reading-toggle-${i}`}
                className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-white/[0.03] transition-colors duration-300"
              >
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">"{r.question}"</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {r.cards.map((c) => (
                      <span key={c.position} className="text-[10px] uppercase tracking-wider px-2 py-1 border"
                        style={{ color: ACCENTS[c.accent], borderColor: `${ACCENTS[c.accent]}55` }}>
                        {c.name}{c.orientation === "reversed" ? " ⟲" : ""}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                  <CaretDown size={16} className={`text-gold transition-transform duration-300 ${open === r.id ? "rotate-180" : ""}`} />
                </div>
              </button>
              {open === r.id && (
                <div className="px-6 pb-6 border-t border-white/10 reading-prose" data-testid={`reading-detail-${i}`}>
                  {renderProse(r.interpretation)}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <h2 className="font-heading text-3xl font-semibold tracking-tight mt-16 mb-6">
        {user?.role === "admin" ? "All Client Sessions" : "My Sessions"}
      </h2>
      {bookings.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center" data-testid="bookings-empty-state">
          <p className="text-sm text-muted-foreground">No sessions booked yet.</p>
          <Link to="/book" className="inline-block mt-4 text-xs uppercase tracking-[0.2em] text-gold hover:underline">
            Book a live reading →
          </Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="bookings-list">
          {bookings.map((b, i) => (
            <div key={b.id} className="bg-card border border-white/10 p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`booking-item-${i}`}>
              <div>
                <p className="font-heading text-xl font-semibold">{b.package_name}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {b.date} at {b.time} · {b.currency === "inr" ? "₹" : "$"}{b.price.toFixed(2)}
                  {b.reader_name ? ` · with ${b.reader_name}` : ""}
                  {user?.role === "admin" && ` · ${b.user_name} (${b.user_email})`}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {b.provider === "upi" && (
                  <a
                    href={`${API}/bookings/${b.id}/screenshot`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`booking-proof-link-${i}`}
                    className="text-[10px] uppercase tracking-widest text-gold hover:underline"
                  >
                    Payment proof
                  </a>
                )}
                {user?.role === "admin" && b.provider === "upi" && b.status === "pending_verification" && (
                  <>
                    <button
                      onClick={() => verifyBooking(b.id, true)}
                      data-testid={`booking-approve-${i}`}
                      className="px-4 py-1.5 border border-work/50 text-work text-[10px] uppercase tracking-widest hover:bg-work hover:text-black transition-colors duration-300"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => verifyBooking(b.id, false)}
                      data-testid={`booking-reject-${i}`}
                      className="px-4 py-1.5 border border-red-500/50 text-red-400 text-[10px] uppercase tracking-widest hover:bg-red-500 hover:text-black transition-colors duration-300"
                    >
                      Reject
                    </button>
                  </>
                )}
                <span className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${STATUS_STYLES[b.status] || "text-muted-foreground border-white/20"}`}>
                  {b.status.replace(/_/g, " ")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
